/**
 * Information for using `createPortal` functionality addapted for modals
 * was taken from https://medium.com/@imranhsayed/create-modal-in-react-using-portal-48b3662a28b9
 */
import React, { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Background, CloseButton, Frame, Wrapper } from './Modal.style'

const modalRoot = document.getElementById('modal')

function Modal(props) {
    const { children, confirm, height, isOpen, minHeight, minWidth, onClick, onClose, scrollable, width, ...rest } = props

    // We create an element div for this modal
    const element = useMemo(() => document.createElement('div'), [])

    useEffect(() => {
        // Append the modal to the parent element
        modalRoot.appendChild(element)

        return () => {
            modalRoot.removeChild(element)
        }
    }, [element])

    const onClickModal = (e) => {
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        if (typeof onClick === 'function') {
            onClick()
        }
    }

    const renderModal = () => (
        <>
            {isOpen && (
                <Wrapper confirm={confirm} onClick={onClickModal} {...rest}>
                    <Background onClick={onClose} />
                    <Frame
                        height={height}
                        width={width}
                        minHeight={minHeight}
                        minWidth={minWidth}
                        open={isOpen}
                        onClick={(e) => e.preventDefault()}
                        confirm={confirm}
                        scrollable={scrollable}
                    >
                        <CloseButton onClick={onClose}>
                            <FontAwesomeIcon icon="times" />
                        </CloseButton>
                        {children}
                    </Frame>
                </Wrapper>
            )}
        </>
    )

    return createPortal(renderModal(), element)
}

Modal.propTypes = {
    children: PropTypes.node.isRequired,
    confirm: PropTypes.bool,
    coordinates: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
    height: PropTypes.string,
    isOpen: PropTypes.bool,
    minHeight: PropTypes.string,
    minWidth: PropTypes.string,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    scrollable: PropTypes.bool,
    width: PropTypes.string,
}

Modal.defaultProps = {
    confirm: false,
    coordinates: {
        x: 0,
        y: 0,
    },
    height: null,
    isOpen: false,
    minHeight: null,
    minWidth: null,
    onClick: () => null,
    onClose: () => null,
    scrollable: false,
    width: null,
}

export default Modal
